import React, { useState } from 'react';
import {
  FiUpload, FiFileText, FiCheckCircle, FiAlertCircle, FiX, FiShield, FiLock, FiLoader
} from 'react-icons/fi';
import toast from 'react-hot-toast';
import docsApi from '../../api/docsApi';

export function ApiUploadModal({ existingDoc, onClose, onSuccess }) {
  const isNewVersionMode = Boolean(existingDoc);

  const [inputMode, setInputMode] = useState('file'); // 'file' | 'raw'
  const [selectedFile, setSelectedFile] = useState(null);
  const [rawJson, setRawJson] = useState('');
  const [title, setTitle] = useState(existingDoc?.title || '');
  const [description, setDescription] = useState(existingDoc?.description || '');
  const [moduleName, setModuleName] = useState(existingDoc?.moduleName || 'Tech');
  const [tags, setTags] = useState(existingDoc?.tags || '');
  const [versionNumber, setVersionNumber] = useState('');
  const [changelog, setChangelog] = useState('');
  const [visibility, setVisibility] = useState(existingDoc?.visibility || 'ORGANIZATION');

  const [isValidating, setIsValidating] = useState(false);
  const [validationResult, setValidationResult] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.name.endsWith('.json')) {
      toast.error('Only OpenAPI specification JSON files are supported (.json)');
      return;
    }

    setSelectedFile(file);
    setErrorMessage(null);
    setIsValidating(true);

    try {
      const res = await docsApi.validateDocument(file, undefined);
      setValidationResult(res);
      if (res.valid) {
        if (!title && res.title) setTitle(res.title);
        if (!description && res.description) setDescription(res.description);
        if (!versionNumber && res.version) setVersionNumber(res.version);
        toast.success(`Valid OpenAPI: ${res.endpointsCount} endpoints detected!`);
      } else {
        toast.error('Specification validation issues detected');
      }
    } catch (err) {
      setErrorMessage(err.message || 'Validation failed');
    } finally {
      setIsValidating(false);
    }
  };

  const handleValidateRawJson = async () => {
    if (!rawJson.trim()) return;
    setErrorMessage(null);
    setIsValidating(true);

    try {
      const res = await docsApi.validateDocument(undefined, rawJson);
      setValidationResult(res);
      if (res.valid) {
        if (!title && res.title) setTitle(res.title);
        if (!description && res.description) setDescription(res.description);
        if (!versionNumber && res.version) setVersionNumber(res.version);
        toast.success(`Valid OpenAPI: ${res.endpointsCount} endpoints detected!`);
      } else {
        toast.error('Specification validation issues detected');
      }
    } catch (err) {
      setErrorMessage(err.message || 'Validation failed');
    } finally {
      setIsValidating(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedFile && !rawJson.trim()) {
      setErrorMessage('Please select an OpenAPI JSON file or paste JSON content.');
      return;
    }

    setIsSubmitting(true);
    setErrorMessage(null);

    try {
      const formData = new FormData();
      if (selectedFile) {
        formData.append('file', selectedFile);
      }
      if (title.trim()) formData.append('title', title.trim());
      if (description.trim()) formData.append('description', description.trim());
      if (moduleName.trim()) formData.append('moduleName', moduleName.trim());
      if (tags.trim()) formData.append('tags', tags.trim());
      if (versionNumber.trim()) formData.append('versionNumber', versionNumber.trim());
      if (changelog.trim()) formData.append('changelog', changelog.trim());
      formData.append('visibility', visibility);
      if (!selectedFile && rawJson.trim()) {
        formData.append('openApiJson', rawJson.trim());
      }

      let result;
      if (isNewVersionMode) {
        result = await docsApi.uploadVersion(existingDoc.id, formData);
        toast.success(`Version ${result?.currentVersion || versionNumber || 'new'} published successfully!`);
      } else {
        result = await docsApi.uploadDocument(formData);
        toast.success('API Documentation published successfully!');
      }

      onSuccess(result);
      onClose();
    } catch (err) {
      setErrorMessage(err.message || 'Failed to upload documentation');
      toast.error(err.message || 'Upload failed');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm">
      <div className="relative w-full max-w-2xl max-h-[90vh] flex flex-col rounded-2xl bg-white border border-slate-200 shadow-2xl overflow-hidden text-slate-900">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100 bg-slate-50/50">
          <div>
            <h2 className="text-base font-bold text-slate-900 flex items-center gap-2">
              <FiUpload className="text-blue-600" />
              {isNewVersionMode ? `Upload New Version for "${existingDoc.title}"` : 'Upload API Documentation'}
            </h2>
            <p className="text-xs text-slate-500 mt-0.5">
              Upload OpenAPI / Swagger JSON document to automatically publish interactive documentation.
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition"
          >
            <FiX size={18} />
          </button>
        </div>

        {/* Form Body */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6 space-y-5 custom-scrollbar">
          {/* File vs Raw Tabs */}
          <div className="flex items-center gap-1.5 bg-slate-100 p-1 rounded-xl text-xs font-semibold">
            <button
              type="button"
              onClick={() => setInputMode('file')}
              className={`flex-1 py-1.5 rounded-lg text-center transition ${
                inputMode === 'file'
                  ? 'bg-white text-slate-900 shadow-sm'
                  : 'text-slate-500 hover:text-slate-900'
              }`}
            >
              Upload OpenAPI File (.json)
            </button>
            <button
              type="button"
              onClick={() => setInputMode('raw')}
              className={`flex-1 py-1.5 rounded-lg text-center transition ${
                inputMode === 'raw'
                  ? 'bg-white text-slate-900 shadow-sm'
                  : 'text-slate-500 hover:text-slate-900'
              }`}
            >
              Paste Raw OpenAPI JSON
            </button>
          </div>

          {inputMode === 'file' ? (
            <div>
              <label className="block text-xs font-bold text-slate-700 mb-1.5">
                OpenAPI Specification File
              </label>
              <div className="relative border-2 border-dashed border-slate-200 hover:border-blue-400 rounded-xl p-6 text-center cursor-pointer transition bg-slate-50/50 hover:bg-blue-50/20">
                <input
                  type="file"
                  accept=".json,application/json"
                  onChange={handleFileChange}
                  className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                />
                <div className="flex flex-col items-center gap-2">
                  <div className="w-10 h-10 rounded-full bg-blue-50 text-blue-600 flex items-center justify-center">
                    <FiFileText size={20} />
                  </div>
                  {selectedFile ? (
                    <div>
                      <p className="text-sm font-semibold text-slate-900">{selectedFile.name}</p>
                      <p className="text-xs text-slate-500">
                        {(selectedFile.size / 1024).toFixed(1)} KB · Click to change file
                      </p>
                    </div>
                  ) : (
                    <div>
                      <p className="text-sm font-medium text-slate-800">
                        Drop your OpenAPI JSON file here, or <span className="text-blue-600 font-semibold">browse</span>
                      </p>
                      <p className="text-[11px] text-slate-500 mt-0.5">Supports OpenAPI 3.0, 3.1, and Swagger 2.0 JSON (up to 10MB)</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ) : (
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label className="text-xs font-bold text-slate-700">
                  OpenAPI Specification JSON
                </label>
                <button
                  type="button"
                  onClick={handleValidateRawJson}
                  disabled={isValidating || !rawJson.trim()}
                  className="text-xs text-blue-600 hover:text-blue-700 font-semibold disabled:opacity-50"
                >
                  {isValidating ? 'Validating...' : 'Validate JSON'}
                </button>
              </div>
              <textarea
                rows={6}
                value={rawJson}
                onChange={(e) => setRawJson(e.target.value)}
                placeholder='{\n  "openapi": "3.0.0",\n  "info": { "title": "My API", "version": "1.0.0" },\n  "paths": { ... }\n}'
                className="w-full text-xs font-mono p-3 rounded-xl border border-slate-800 bg-slate-900 text-emerald-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                spellCheck={false}
              />
            </div>
          )}

          {/* Validation Feedback Banner */}
          {isValidating && (
            <div className="flex items-center gap-2 p-3 rounded-xl bg-slate-100 text-slate-700 text-xs">
              <FiLoader size={16} className="animate-spin text-blue-600" />
              <span>Checking OpenAPI document structure and endpoints...</span>
            </div>
          )}

          {validationResult && (
            <div
              className={`p-3.5 rounded-xl border text-xs ${
                validationResult.valid
                  ? 'bg-emerald-50 border-emerald-200 text-emerald-800'
                  : 'bg-rose-50 border-rose-200 text-rose-800'
              }`}
            >
              <div className="flex items-center gap-2 font-bold">
                {validationResult.valid ? (
                  <>
                    <FiCheckCircle size={16} className="text-emerald-600" />
                    <span>Valid OpenAPI Specification</span>
                  </>
                ) : (
                  <>
                    <FiAlertCircle size={16} className="text-rose-600" />
                    <span>OpenAPI Validation Issues</span>
                  </>
                )}
              </div>

              {validationResult.valid ? (
                <div className="mt-2 space-y-1 text-slate-700">
                  <p>
                    <span className="font-semibold text-slate-900">Extracted Title:</span>{' '}
                    {validationResult.title || 'Untitled'} (v{validationResult.version || '1.0.0'})
                  </p>
                  <p>
                    <span className="font-semibold text-slate-900">Endpoints Detected:</span>{' '}
                    {validationResult.endpointsCount} endpoints found
                  </p>
                </div>
              ) : (
                <ul className="mt-2 list-disc list-inside space-y-0.5 text-rose-700">
                  {validationResult.errors.map((err, i) => (
                    <li key={i}>{err}</li>
                  ))}
                </ul>
              )}
            </div>
          )}

          {/* Metadata Fields */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-bold text-slate-700 mb-1">
                API Title / Name
              </label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="e.g. Leave Management Service"
                className="w-full text-xs px-3 py-2 rounded-lg border border-slate-200 bg-white text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-600 transition"
              />
            </div>

            <div>
              <label className="block text-xs font-bold text-slate-700 mb-1">
                Version Number
              </label>
              <input
                type="text"
                value={versionNumber}
                onChange={(e) => setVersionNumber(e.target.value)}
                placeholder="e.g. 1.0.0 or 2.1.0"
                className="w-full text-xs font-mono px-3 py-2 rounded-lg border border-slate-200 bg-white text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-600 transition"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-bold text-slate-700 mb-1">
                ERP Module
              </label>
              <input
                type="text"
                value={moduleName}
                onChange={(e) => setModuleName(e.target.value)}
                placeholder="e.g. Finance, HR, Sales, Tech"
                list="module-options"
                className="w-full text-xs px-3 py-2 rounded-lg border border-slate-200 bg-white text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-600 transition"
              />
              <datalist id="module-options">
                <option value="Tech" />
                <option value="Finance" />
                <option value="HR" />
                <option value="Sales" />
                <option value="Production" />
                <option value="Core" />
              </datalist>
            </div>

            <div>
              <label className="block text-xs font-bold text-slate-700 mb-1">
                Tags (comma separated)
              </label>
              <input
                type="text"
                value={tags}
                onChange={(e) => setTags(e.target.value)}
                placeholder="e.g. auth, jwt, payments"
                className="w-full text-xs px-3 py-2 rounded-lg border border-slate-200 bg-white text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-600 transition"
              />
            </div>
          </div>

          {/* Visibility Selection */}
          <div>
            <label className="block text-xs font-bold text-slate-700 mb-1.5">
              Access Visibility
            </label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <label
                className={`flex items-start gap-3 p-3 rounded-xl border cursor-pointer transition ${
                  visibility === 'ORGANIZATION'
                    ? 'border-blue-500 bg-blue-50/40 shadow-sm'
                    : 'border-slate-200 hover:border-slate-300 bg-white'
                }`}
              >
                <input
                  type="radio"
                  name="visibility"
                  value="ORGANIZATION"
                  checked={visibility === 'ORGANIZATION'}
                  onChange={() => setVisibility('ORGANIZATION')}
                  className="mt-0.5 text-blue-600"
                />
                <div>
                  <p className="text-xs font-bold text-slate-900 flex items-center gap-1.5">
                    <FiShield size={13} className="text-blue-600" /> Organization
                  </p>
                  <p className="text-[11px] text-slate-500 leading-normal mt-0.5">
                    All authenticated users in the company ERP can view and explore this documentation.
                  </p>
                </div>
              </label>

              <label
                className={`flex items-start gap-3 p-3 rounded-xl border cursor-pointer transition ${
                  visibility === 'PRIVATE'
                    ? 'border-blue-500 bg-blue-50/40 shadow-sm'
                    : 'border-slate-200 hover:border-slate-300 bg-white'
                }`}
              >
                <input
                  type="radio"
                  name="visibility"
                  value="PRIVATE"
                  checked={visibility === 'PRIVATE'}
                  onChange={() => setVisibility('PRIVATE')}
                  className="mt-0.5 text-blue-600"
                />
                <div>
                  <p className="text-xs font-bold text-slate-900 flex items-center gap-1.5">
                    <FiLock size={13} className="text-blue-600" /> Private
                  </p>
                  <p className="text-[11px] text-slate-500 leading-normal mt-0.5">
                    Only you and specifically granted ERP users can access or view this documentation.
                  </p>
                </div>
              </label>
            </div>
          </div>

          <div>
            <label className="block text-xs font-bold text-slate-700 mb-1">
              Description
            </label>
            <textarea
              rows={2}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Brief description of the API purpose and services..."
              className="w-full text-xs px-3 py-2 rounded-lg border border-slate-200 bg-white text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-600 transition"
            />
          </div>

          {isNewVersionMode && (
            <div>
              <label className="block text-xs font-bold text-slate-700 mb-1">
                Version Release Notes / Changelog
              </label>
              <input
                type="text"
                value={changelog}
                onChange={(e) => setChangelog(e.target.value)}
                placeholder="e.g. Added customer refund endpoints and updated response schemas"
                className="w-full text-xs px-3 py-2 rounded-lg border border-slate-200 bg-white text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-600 transition"
              />
            </div>
          )}

          {errorMessage && (
            <div className="flex items-center gap-2 p-3 rounded-xl bg-rose-50 text-rose-700 text-xs border border-rose-200">
              <FiAlertCircle size={15} />
              <span>{errorMessage}</span>
            </div>
          )}

          {/* Footer Actions */}
          <div className="flex items-center justify-end gap-3 pt-3 border-t border-slate-100">
            <button
              type="button"
              onClick={onClose}
              disabled={isSubmitting}
              className="px-4 py-2 text-xs font-medium text-slate-700 hover:text-slate-900 bg-white hover:bg-slate-50 border border-slate-200 rounded-lg transition"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting || (!selectedFile && !rawJson.trim())}
              className="inline-flex items-center gap-2 px-5 py-2 text-xs font-semibold text-white bg-blue-600 hover:bg-blue-700 rounded-lg shadow-sm transition disabled:opacity-50"
            >
              {isSubmitting ? <FiLoader size={14} className="animate-spin" /> : <FiUpload size={14} />}
              <span>{isSubmitting ? 'Publishing Documentation...' : isNewVersionMode ? 'Publish New Version' : 'Publish Documentation'}</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default ApiUploadModal;
